import { useCallback, useEffect, useMemo, useRef } from 'react';
import type {
  CaptureChargesRequest,
  RecordPaymentRequest,
} from '@store/generated/billing';
import type { ClaimScrubRequest, CreateClaimRequest } from '@store/generated/claims';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import type { ClaimUpdateRequest, SubmitClaimRequest } from '../types';

import {
  useCaptureChargesMutation,
  useClaimRebuildMutation,
  useClaimScrubMutation,
  useClaimSubmitMutation,
  useCreateClaimMutation,
  useDeleteClaimMutation,
  useGetBillingClaimsListQuery,
  useGetBillingEncountersQuery,
  useGetClaimByIdQuery,
  useGetClaimPdfQuery,
  useGetClaimQueueCountsQuery,
  useGetClaimsQuery,
  useGetEncounterDetailQuery,
  useGetProviderRemittancesQuery,
  useGetRemittanceQueueCountsQuery,
  useGetStatementsHistoryQuery,
  useGetStatementsQueueQuery,
  useRecordPaymentMutation,
  useResubmitClaimMutation,
  useUpdateClaimMutation,
} from './api';
import {
  selectScrubberNavigation,
  selectScrubberPanel,
  selectScrubberPreferences,
} from './selectors';
import {
  clearHighlight,
  closePanel,
  navigateToField,
  openPanel,
  setPanelPosition,
  setPanelWidth,
  togglePanel,
  togglePanelCollapse,
} from './slice';

/**
 * @deprecated Use `useBillingClaimsList` instead — it uses the new org-scoped enriched endpoint.
 */
export function useBillingClaims(
  filters?: {
    patientId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
    sortBy?: 'created_at' | 'service_date_from' | 'claim_number' | 'total_charge_cents';
    sortOrder?: 'asc' | 'desc';
  },
  pollingInterval?: number,
) {
  // Build query params, excluding null/undefined values
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      pageSize: filters?.pageSize ?? 20,
      sortBy: filters?.sortBy ?? 'created_at',
      sortOrder: filters?.sortOrder ?? 'desc',
    };

    // Only add optional params if they have values
    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.status) params.status = filters.status;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetClaimsQuery(queryParams, {
    pollingInterval,
  });

  return {
    claims: data?.claims ?? [],
    totalCount: data?.total ?? 0,
    currentPage: filters?.page ?? 1,
    totalPages: Math.ceil((data?.total ?? 0) / (filters?.pageSize ?? 20)),
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for the new org-scoped enriched claims list.
 * Returns ClaimListItem[] with computed intelligence fields (next_action, readiness, annotation counts).
 */
export function useBillingClaimsList(
  filters?: {
    patientId?: string;
    providerIds?: string;
    status?: string;
    insurancePayerId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    perPage?: number;
    sortBy?: 'created_at' | 'service_date_from' | 'total_charge_cents';
    sortOrder?: 'asc' | 'desc';
  },
  pollingInterval?: number,
) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
      sortBy: filters?.sortBy ?? 'created_at',
      sortOrder: filters?.sortOrder ?? 'desc',
    };

    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.providerIds) params.providerIds = filters.providerIds;
    if (filters?.status) params.status = filters.status;
    if (filters?.insurancePayerId) params.insurancePayerId = filters.insurancePayerId;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetBillingClaimsListQuery(queryParams, {
    pollingInterval,
  });

  return {
    claims: data?.claims ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for claim queue badge counts (needs_attention, ready_to_submit, etc.).
 * Accepts the same filters as the claims list (except status) to keep counts in sync.
 */
export function useClaimQueueCounts(filters?: {
  patientId?: string;
  providerIds?: string;
  insurancePayerId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.providerIds) params.providerIds = filters.providerIds;
    if (filters?.insurancePayerId) params.insurancePayerId = filters.insurancePayerId;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetClaimQueueCountsQuery(queryParams);

  return {
    counts: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for managing billing encounters (org-scoped)
 */
export function useBillingEncounters(filters?: {
  providerIds?: string;
  patientId?: string;
  status?: string;
  billable?: boolean;
  hasClaim?: boolean;
  readyToBill?: string;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  perPage?: number;
  sortBy?: 'scheduled_for' | 'created_at';
  sortOrder?: 'asc' | 'desc';
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
      sortBy: filters?.sortBy ?? 'scheduled_for',
      sortOrder: filters?.sortOrder ?? 'desc',
    };

    if (filters?.providerIds) params.providerIds = filters.providerIds;
    if (filters?.patientId) params.patientId = filters.patientId;
    if (filters?.status) params.status = filters.status;
    if (filters?.billable !== undefined) params.billable = filters.billable;
    if (filters?.hasClaim !== undefined) params.hasClaim = filters.hasClaim;
    if (filters?.readyToBill) params.readyToBill = filters.readyToBill;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetBillingEncountersQuery(queryParams);

  return {
    encounters: data?.encounters ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for the remittances list with enriched data, filters, sorting, and queue support.
 * Mirrors the useBillingClaimsList pattern.
 */
export function useRemittancesList(filters?: {
  dateFrom?: string;
  dateTo?: string;
  payerName?: string;
  paymentMethod?: string;
  queue?: string;
  page?: number;
  perPage?: number;
  sortBy?: 'payment_date' | 'total_payment_amount_cents' | 'created_at' | 'payer_name';
  sortOrder?: 'asc' | 'desc';
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 25,
    };

    if (filters?.sortBy) params.sortBy = filters.sortBy;
    if (filters?.sortOrder) params.sortOrder = filters.sortOrder;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.payerName) params.payerName = filters.payerName;
    if (filters?.paymentMethod) params.paymentMethod = filters.paymentMethod;
    if (filters?.queue) params.queue = filters.queue;

    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetProviderRemittancesQuery(queryParams);

  return {
    remittances: data?.remittances ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 25,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for remittance queue badge counts (all, has_denials, reversals, clean, imported).
 * Accepts the same filters as the remittances list (except queue) to keep counts in sync.
 */
export function useRemittanceQueueCounts(filters?: {
  dateFrom?: string;
  dateTo?: string;
  payerName?: string;
  paymentMethod?: string;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string> = {};
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    if (filters?.payerName) params.payerName = filters.payerName;
    if (filters?.paymentMethod) params.paymentMethod = filters.paymentMethod;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetRemittanceQueueCountsQuery(queryParams);

  return {
    counts: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for the patient statements queue — patients with confirmed balances ready for billing.
 */
export function useStatementsQueue(filters?: {
  page?: number;
  perPage?: number;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
    };
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetStatementsQueueQuery(queryParams);

  return {
    items: data?.items ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for statement history — completed and failed statement runs.
 */
export function useStatementsHistory(filters?: {
  page?: number;
  perPage?: number;
  triggerType?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const queryParams = useMemo(() => {
    const params: Record<string, string | number> = {
      page: filters?.page ?? 1,
      perPage: filters?.perPage ?? 20,
    };
    if (filters?.triggerType) params.triggerType = filters.triggerType;
    if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
    if (filters?.dateTo) params.dateTo = filters.dateTo;
    return params;
  }, [filters]);

  const { data, isLoading, isFetching, error, refetch } = useGetStatementsHistoryQuery(queryParams);

  return {
    items: data?.items ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.page ?? 1,
    totalPages: data?.total_pages ?? 1,
    perPage: data?.per_page ?? 20,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for fetching a single encounter detail
 */
export function useEncounterDetail(encounterId?: string) {
  const { data, isLoading, isFetching, error, refetch } = useGetEncounterDetailQuery(
    { encounterId: encounterId ?? '' },
    { skip: !encounterId },
  );

  return {
    encounter: data ?? null,
    isLoading,
    isFetching,
    error,
    refetch,
  };
}

/**
 * Hook for claim submission and management
 */
export function useClaimSubmission() {
  const [createClaim, { isLoading: isCreating }] = useCreateClaimMutation();
  const [updateClaim, { isLoading: isUpdating }] = useUpdateClaimMutation();
  const [deleteClaim, { isLoading: isDeleting }] = useDeleteClaimMutation();

  const submitClaim = useCallback(
    async (claimData: CreateClaimRequest) => {
      try {
        const result = await createClaim({ createClaimRequest: claimData }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to submit claim:', error);
        return { success: false, error };
      }
    },
    [createClaim],
  );

  const updateExistingClaim = useCallback(
    async (claimId: string, updates: ClaimUpdateRequest) => {
      try {
        const result = await updateClaim({
          claimId,
          updateClaimRequest: updates,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to update claim:', error);
        return { success: false, error };
      }
    },
    [updateClaim],
  );

  const deleteExistingClaim = useCallback(
    async (claimId: string) => {
      try {
        await deleteClaim({ claimId }).unwrap();
        return { success: true };
      } catch (error) {
        console.error('Failed to delete claim:', error);
        return { success: false, error };
      }
    },
    [deleteClaim],
  );

  return {
    submitClaim,
    updateClaim: updateExistingClaim,
    deleteClaim: deleteExistingClaim,
    isSubmitting: isCreating || isUpdating || isDeleting,
  };
}

/**
 * Hook for getting a single claim by ID
 */
export function useClaimDetails(claimId?: string) {
  const { data, isLoading, error, refetch } = useGetClaimByIdQuery(
    { claimId: claimId ?? '' },
    { skip: !claimId },
  );

  return {
    claim: data,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Hook for generating claim PDF
 */
export function useClaimPdf(claimId?: string) {
  const { data, isLoading, error, refetch } = useGetClaimPdfQuery(
    { claimId: claimId ?? '' },
    { skip: !claimId },
  );

  const downloadPdf = useCallback(() => {
    if (!data?.pdf_data) return;

    // Convert base64 to blob and open in new window
    const byteCharacters = atob(data.pdf_data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
  }, [data?.pdf_data]);

  return {
    pdfData: data?.pdf_data,
    filename: data?.filename,
    isGenerating: isLoading,
    error,
    downloadPdf,
    regenerate: refetch,
  };
}

/**
 * Hook for scrubbing/validating a claim
 */
export function useClaimScrub() {
  const [scrubClaim, { isLoading: isScrubbing }] = useClaimScrubMutation();

  const performScrub = useCallback(
    async (claimId: string, scrubData?: ClaimScrubRequest) => {
      try {
        const result = await scrubClaim({
          claimId,
          claimScrubRequest: scrubData || {},
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to scrub claim:', error);
        return { success: false, error };
      }
    },
    [scrubClaim],
  );

  return {
    scrubClaim: performScrub,
    isScrubbing,
  };
}

/**
 * Hook for submitting a claim to clearinghouse
 */
export function useClaimSubmit() {
  const [submitClaimMutation, { isLoading: isSubmitting }] = useClaimSubmitMutation();
  const { openPanel } = useClaimScrubberUI();

  const performSubmit = useCallback(
    async (claimId: string, submitData?: SubmitClaimRequest) => {
      try {
        const result = await submitClaimMutation({
          claimId,
          submitClaimRequest: submitData || {},
        }).unwrap();

        // Open panel if submission was blocked and there are annotations
        if (result?.submission_blocked && result?.claim?.annotations?.length) {
          openPanel();
        }

        return {
          success: true,
          data: result,
          submissionBlocked: result?.submission_blocked || false,
        };
      } catch (error) {
        console.error('Failed to submit claim:', error);
        return { success: false, error, submissionBlocked: false };
      }
    },
    [submitClaimMutation, openPanel],
  );

  return {
    submitClaim: performSubmit,
    isSubmitting,
  };
}

/**
 * Hook for rebuilding a claim (re-pulling source data)
 */
export function useClaimRebuild() {
  const [rebuildClaimMutation, { isLoading: isRebuilding }] = useClaimRebuildMutation();

  const rebuildClaim = useCallback(
    async (claimId: string) => {
      try {
        const result = await rebuildClaimMutation({
          claimId,
          rebuildClaimRequest: {},
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to rebuild claim:', error);
        return { success: false, error };
      }
    },
    [rebuildClaimMutation],
  );

  return {
    rebuildClaim,
    isRebuilding,
  };
}

/**
 * Hook for creating correction (code 7) or void (code 8) claims.
 * Creates a new draft claim derived from an existing submitted claim.
 */
export function useResubmitClaim() {
  const [resubmitMutation, { isLoading: isResubmitting }] = useResubmitClaimMutation();

  const resubmitClaim = useCallback(
    async (claimId: string, code: '7' | '8', originalRefNumber?: string) => {
      try {
        const result = await resubmitMutation({
          claimId,
          createResubmissionClaimRequest: {
            resubmission_code: code,
            original_reference_number: originalRefNumber || undefined,
          },
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to resubmit claim:', error);
        return { success: false, error };
      }
    },
    [resubmitMutation],
  );

  return {
    resubmitClaim,
    isResubmitting,
  };
}

/**
 * Combined hook for all billing data
 */
export function useBillingData(filters?: {
  patientId?: string;
  dateFrom?: string;
  dateTo?: string;
}) {
  const claims = useBillingClaims(filters);
  const encounters = useBillingEncounters({
    ...filters,
    status: 'pending',
  });

  const stats = useMemo(
    () => ({
      totalClaims: claims.totalCount,
      unbilledEncounters: encounters.totalCount,
      draftClaims: claims.claims.filter(c => c.status === 'draft').length,
      submittedClaims: claims.claims.filter(c => ['submitted', 'acknowledged', 'accepted', 'rejected'].includes(c.status ?? '')).length,
    }),
    [claims.totalCount, claims.claims, encounters.totalCount],
  );

  return {
    claims: claims.claims,
    encounters: encounters.encounters,
    stats,
    isLoading: claims.isLoading || encounters.isLoading,
    refetch: () => {
      claims.refetch();
      encounters.refetch();
    },
  };
}

// ============================================================================
// Patient Payment Hooks
// ============================================================================

/**
 * Hook for recording a manual payment (cash, check, external)
 */
export function useRecordPayment() {
  const [recordPaymentMutation, { isLoading, error }] = useRecordPaymentMutation();

  const recordPayment = useCallback(
    async (patientId: string, payment: RecordPaymentRequest) => {
      try {
        const result = await recordPaymentMutation({
          patientId,
          recordPaymentRequest: payment,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to record payment:', error);
        return { success: false, error };
      }
    },
    [recordPaymentMutation],
  );

  return { recordPayment, isLoading, error };
}

/**
 * Hook for capturing charges (self-pay or any patient billing)
 */
export function useCaptureCharges() {
  const [captureChargesMutation, { isLoading, error }] = useCaptureChargesMutation();

  const captureCharges = useCallback(
    async (charges: CaptureChargesRequest) => {
      try {
        const result = await captureChargesMutation({
          captureChargesRequest: charges,
        }).unwrap();
        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to capture charges:', error);
        return { success: false, error };
      }
    },
    [captureChargesMutation],
  );

  return { captureCharges, isLoading, error };
}


// ============================================================================
// Claim Scrubber UI Hooks
// ============================================================================

/**
 * Hook for managing ClaimScrubber UI state
 * This hook only manages UI state - server state is handled by RTK Query
 */
/**
 * Smoothly scrolls an element to the center of the viewport
 *
 * @param element - The HTML element to scroll into view
 * @returns Promise that resolves when scrolling completes
 *
 * @remarks
 * Uses requestAnimationFrame to efficiently detect scroll completion by monitoring
 * when the scroll position stabilizes (movement < 0.5px for 3+ consecutive frames).
 * If no scroll movement is detected within 400ms (element already visible or scroll
 * failed), resolves immediately. Otherwise, lets the scroll complete naturally.
 */
const smoothScrollToCenter = (element: HTMLElement): Promise<void> => {
  return new Promise(resolve => {
    // Get the scrollable container (root-main) or fall back to window
    const scrollContainer = document.getElementById('root-main');

    // Early exit if element doesn't exist
    if (!element) {
      resolve();
      return;
    }

    let lastPosition = -1;
    let animationId: number | null = null;
    let samePositionCount = 0;
    let hasStartedMoving = false;
    let noMovementTimeout: NodeJS.Timeout | null = null;

    const getScrollPosition = (): number => {
      return scrollContainer ? scrollContainer.scrollTop : window.scrollY;
    };

    const cleanup = () => {
      if (animationId !== null) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
      if (noMovementTimeout !== null) {
        clearTimeout(noMovementTimeout);
        noMovementTimeout = null;
      }
    };

    const checkScroll = () => {
      const currentPosition = getScrollPosition();
      const delta = Math.abs(currentPosition - lastPosition);

      // Check if scroll has started moving
      if (!hasStartedMoving && currentPosition !== lastPosition) {
        hasStartedMoving = true;
        // Cancel the no-movement timeout since scroll is working
        if (noMovementTimeout !== null) {
          clearTimeout(noMovementTimeout);
          noMovementTimeout = null;
        }
      }

      // Only check for completion after scroll has started
      if (hasStartedMoving) {
        // Check if position is barely changing (< 0.5px movement)
        // This handles sub-pixel movements during easing
        if (delta < 0.5) {
          samePositionCount++;
          
          // Position stable for 3+ frames (~50ms) means scroll completed
          // This provides a small buffer to ensure easing has fully completed
          if (samePositionCount >= 3) {
            cleanup();
            resolve();
            return;
          }
        } else {
          // Position changed significantly, reset counter
          samePositionCount = 0;
        }
      }

      lastPosition = currentPosition;

      // Continue monitoring
      animationId = requestAnimationFrame(checkScroll);
    };

    // Set up timeout for case where scroll never starts
    // (element already visible or scroll fails)
    noMovementTimeout = setTimeout(() => {
      if (!hasStartedMoving) {
        cleanup();
        resolve();
      }
    }, 400);

    // Store initial position
    lastPosition = getScrollPosition();

    // Trigger smooth scroll
    try {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest',
      });
    } catch {
      // Fallback if scrollIntoView fails
      cleanup();
      resolve();
      return;
    }

    // Start monitoring scroll position
    animationId = requestAnimationFrame(checkScroll);
  });
};

export function useClaimScrubberUI() {
  const dispatch = useAppDispatch();
  const panel = useAppSelector(selectScrubberPanel);
  const navigation = useAppSelector(selectScrubberNavigation);
  const preferences = useAppSelector(selectScrubberPreferences);
  const activeBoxElementRef = useRef<HTMLElement | null>(null);

  // Panel actions
  const handleTogglePanel = useCallback(() => {
    dispatch(togglePanel());
  }, [dispatch]);

  const handleOpenPanel = useCallback(() => {
    dispatch(openPanel());
  }, [dispatch]);

  const handleClosePanel = useCallback(() => {
    dispatch(closePanel());
  }, [dispatch]);

  const handleSetPosition = useCallback(
    (position: 'bottom' | 'sidebar') => {
      dispatch(setPanelPosition(position));
    },
    [dispatch],
  );

  const handleToggleCollapse = useCallback(() => {
    dispatch(togglePanelCollapse());
  }, [dispatch]);

  const handleSetPanelWidth = useCallback(
    (width: number) => {
      dispatch(setPanelWidth(width));
    },
    [dispatch],
  );

  // Navigation actions
  const handleNavigateToField = useCallback(
    (field: string, box?: string | null) => {
      dispatch(navigateToField({ field, box }));

      // Clean up any previous highlight on box element
      if (activeBoxElementRef.current) {
        activeBoxElementRef.current.classList.remove('highlighted-box');
        activeBoxElementRef.current = null;
      }

      const fieldElement = document.querySelector(`[name="${field}"]`) as HTMLElement;
      
      // Determine which box to highlight
      let boxElement: HTMLElement | null = null;
      const boxNumber = box || navigation.highlightedBox;
      
      if (boxNumber) {
        // Try to find box by explicit box number
        boxElement = document.querySelector(`[data-cms-box="${boxNumber}"]`) as HTMLElement;
      }
      
      // If no box found yet and we have a field element, traverse DOM to find parent box
      if (!boxElement && fieldElement) {
        let parent: HTMLElement | null = fieldElement.parentElement;
        while (parent) {
          if (parent.hasAttribute('data-cms-box')) {
            boxElement = parent;
            break;
          }
          parent = parent.parentElement;
        }
      }
      
      const targetElement = boxElement || fieldElement;

      if (!targetElement || !fieldElement) return;

      // Store reference for cleanup (only box elements get highlighted)
      if (boxElement) {
        activeBoxElementRef.current = boxElement;
      }

      // Always scroll to center the element, then highlight after completion
      smoothScrollToCenter(targetElement).then(() => {
        // Scroll animation complete, now apply highlight and focus
        fieldElement.focus();
        if (boxElement) {
          boxElement.classList.add('highlighted-box');
        }
      });

      // Remove highlight using CSS animation end event (only for box elements)
      if (boxElement) {
        boxElement.addEventListener(
          'animationend',
          function handleAnimationEnd(e) {
            if (e.animationName === 'highlight-pulse') {
              boxElement.classList.remove('highlighted-box');
              dispatch(clearHighlight());
              if (activeBoxElementRef.current === boxElement) {
                activeBoxElementRef.current = null;
              }
            }
          },
          { once: true },
        );
      }
    },
    [dispatch, navigation.highlightedBox],
  );

  const handleClearHighlight = useCallback(() => {
    dispatch(clearHighlight());
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (activeBoxElementRef.current) {
        activeBoxElementRef.current.classList.remove('highlighted-box');
      }
    };
  }, []);

  return {
    // State
    panel,
    navigation,
    preferences,

    // Actions
    togglePanel: handleTogglePanel,
    openPanel: handleOpenPanel,
    closePanel: handleClosePanel,
    setPosition: handleSetPosition,
    toggleCollapse: handleToggleCollapse,
    setPanelWidth: handleSetPanelWidth,
    togglePanelCollapse: handleToggleCollapse,
    navigateToField: handleNavigateToField,
    clearHighlight: handleClearHighlight,
  };
}

/**
 * Combined hook for ClaimScrubber functionality
 * Combines UI state management with RTK Query scrubbing
 */
export function useClaimScrubberWithUI() {
  const ui = useClaimScrubberUI();
  const [performScrub, { isLoading: isScrubbing }] = useClaimScrubMutation();

  const scrubClaim = useCallback(
    async (claimId: string, scrubData?: ClaimScrubRequest) => {
      try {
        const result = await performScrub({
          claimId,
          claimScrubRequest: scrubData || {},
        }).unwrap();

        // Always open panel if there are any annotations
        if (result?.annotations?.length) {
          ui.openPanel();
        }

        return { success: true, data: result };
      } catch (error) {
        console.error('Failed to scrub claim:', error);
        return { success: false, error };
      }
    },
    [performScrub, ui],
  );

  return {
    ...ui,
    scrubClaim,
    isScrubbing,
  };
}
